import EventEmitter from 'eventemitter3';
import log from '../utils/logger.utils';
import eventsService from './events.service';
import { ConfigService } from './config.service';
import { ClassInstance } from '../types/class.type';
import { Exercise, ExerciseStation, ExerciseStationVideo, ExerciseTask } from '../types/exercise.type';
import { RenderEvents } from '../types/events.type';
import { RenderItem, RenderItemType } from '../types/render.types';
import { TimerScreenData, WorkoutModulesHeader, WorkoutModulesNotification, WorkoutModulesPersonalTrainer, WorkoutModulesProgressCircle, WorkoutModulesVideos, WorkoutScreenData } from '../types/screen.type';
import { BookingScreenData } from '../types/booking.type';
import { MovementScreenData } from '../types/movement.type';

type EventHandler<T> = (message: T) => void;

export class RenderService extends EventEmitter {
    private logger: log.Logger;
    private screenData?: WorkoutScreenData;
    private bookingScreenData?: BookingScreenData;
    private configService: ConfigService;
    // A map that holds arrays of handlers for different event types
    private eventHandlers: Map<string, EventHandler<any>[]> = new Map();

    public static instance: RenderService;

    private constructor() {
        super();
        this.configService = ConfigService.getInstance();
        this.logger = log.getLogger('RenderService');
        this.registerEventListeners();
        this.logger.info('Initialized');
    }

    public static getInstance(): RenderService {
        if (!RenderService.instance) {
            RenderService.instance = new RenderService();
        }
        return RenderService.instance;
    }

    // Method to add a handler for a specific event type
    public addHandler<T>(eventType: string, handler: EventHandler<T>): void {
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, []);
        }
        this.eventHandlers.get(eventType)?.push(handler);
    }

    // Method to remove a handler for a specific event type
    public removeHandler<T>(eventType: string, handler: EventHandler<T>): void {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            this.eventHandlers.set(eventType, handlers.filter(h => h !== handler));
        }
    }

    // Notify all handlers for a specific event type
    private notifyHandlers<T>(eventType: string, data: T): void {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            handlers.forEach((handler, index) => {
                this.logger.trace(`Handler ${index + 1} for event type "${eventType}" is being called with data:`, data);
                handler(data);
            });
        } else {
            this.logger.debug(`No handlers found for event type "${eventType}".`);
        }
    }

    // Example: registering event listeners for multiple types of events
    public registerEventListeners(): void {
        const handleEvent = (event: RenderEvents, renderItem: RenderItem): void => {
            this.logger.debug(`Received ${event} event with data:`, renderItem);
            if (event === RenderEvents.bookingClassesWrapper) {
                if (renderItem.renderItemType === RenderItemType.BOOKING) {
                    this.bookingScreenData = this.renderItemBooking(renderItem);
                    this.notifyHandlers<BookingScreenData>("BookingScreenData", this.bookingScreenData);
                    return;
                }
                return;
            } else if (event === RenderEvents.classWorkoutStart) {
                this.screenData = this.renderItem(renderItem);
                this.notifyHandlers<WorkoutScreenData>("WorkoutScreenData", this.screenData);
                return;
            } else if (event === RenderEvents.classNextWorkout) {
                this.screenData = this.renderItem(renderItem);
                this.notifyHandlers<WorkoutScreenData>("WorkoutScreenData", this.screenData);
                return;
            } else if (event === RenderEvents.movementDataUpdated) {
                if (renderItem.renderItemType === RenderItemType.MOVEMENT) {
                    let movementScreenData = this.renderItemMovement(renderItem);
                    this.notifyHandlers<MovementScreenData>("MovementScreenData", movementScreenData);
                    return;
                }
                return;
            } else if (event === RenderEvents.timerPreWorkout) {
                if (renderItem.renderItemType === RenderItemType.TIMER_PRE_WORKOUT) {
                    let timerScreenData = this.renderTimerPreWorkout(renderItem);
                    this.notifyHandlers<TimerScreenData>("TimerScreenData", timerScreenData);
                    return;
                }
                return;
            } else if (event === RenderEvents.timerStartWorkout) {
                if (renderItem.renderItemType === RenderItemType.TIMER) {
                    let timerScreenData = renderItem.renderItemInfo?.exerciseTask?.type == 'introduction' ?
                        this.renderTimerPreWorkout(renderItem) :
                        this.renderTimerWorkout(renderItem);
                    this.notifyHandlers<TimerScreenData>("TimerScreenData", timerScreenData);
                    return;
                }
                return;
            }
        };

        const eventsToRegister = [
            RenderEvents.classNextWorkout,
            RenderEvents.classWorkoutAboutToStart,
            RenderEvents.classWorkoutStart,
            RenderEvents.bookingClassesWrapper,
            RenderEvents.movementDataUpdated,
            RenderEvents.timerPreWorkout,
            RenderEvents.timerStartWorkout,
        ];

        eventsToRegister.forEach(event => {
            eventsService.on(event, (renderItem: RenderItem) => handleEvent(event, renderItem));
        });
    }


    // #region Methods
    private renderItemBooking(renderItem: RenderItem): BookingScreenData {
        if (!renderItem) {
            return {};
        }
        let bookingScreenData: BookingScreenData = {
            bookingClasses: renderItem.renderItemInfo?.bookingClassesWrapper?.bookingClasses,
            imgPath: this.configService.getFilesStorageUrl(),
        };
        return bookingScreenData ? bookingScreenData : {};
    }

    private renderItem(renderItem: RenderItem): WorkoutScreenData {
        if (!renderItem) {
            return {};
        }

        switch (renderItem.renderItemType) {
            case RenderItemType.EXERCISE_NEXT_WORKOUT:
                return this.renderNextWorkout(renderItem);
            case RenderItemType.EXERCISE_ABOUT_TO_START:
                return this.renderAboutToStartWorkout(renderItem);
            case RenderItemType.EXERCISE:
                return this.renderWorkout(renderItem);
            default:
                this.logger.debug('No renderItemType data', renderItem, renderItem.renderItemType);
                return {};
        }

    }

    private renderItemMovement(renderItem: RenderItem): MovementScreenData {

        if (!renderItem || !renderItem.classSchedule) {
            return {};
        }
        let fs = this.configService.getFilesStorageUrl()
        let cs = renderItem.classSchedule;

        let workoutImage = cs.workout.image ? fs + cs.workout.image : '/img/default_workout_120x120.jpg';
        let movementImage = cs.workout.movement.image ? fs + cs.workout.movement.image : '/img/default_movement.jpeg';
        const task = renderItem.renderItemInfo?.exerciseTask;
        let movementScreenData: MovementScreenData = {
            workoutImage: workoutImage,
            totalSets: task?.is_group_routine ? 1 : task?.exercises_total,
            hydrations: task?.hydrate_total,
            hydrationTime: cs.workout.hydration_break,
            stations: cs.workout.movement.stations,
            pods: cs.workout.movement.pods,
            movementImage: movementImage,
            name: cs.workout.movement.name,
            description: cs.workout.description,
        };
        return movementScreenData ? movementScreenData : {};

    }

    private renderNextWorkout(workoutRenderItem: RenderItem): WorkoutScreenData {
        if (!workoutRenderItem) {
            return {};
        }
        const layoutType = workoutRenderItem.classSchedule?.workout.movement?.movement_tv_layouts?.
            find(l => l.tv_id == this.configService.getTvId())?.layout_type;
        this.screenData = {
            header: this.composeWorkoutModulesHeader(workoutRenderItem),
            progressCircle: this.composeProgressCircle(workoutRenderItem),
            videos: this.composeWorkoutModulesVideos(workoutRenderItem),
            personalTrainer: this.composeWorkoutModulesPersonalTrainer(workoutRenderItem),
            layoutType: layoutType
        };
        this.logger.debug('Rendered next workout with data', this.screenData);
        return this.screenData ? this.screenData : {};

    }

    private renderTimerPreWorkout(renderItem: RenderItem): TimerScreenData {
        if (!renderItem || !renderItem.classSchedule) {
            return {};
        }
        let fs = this.configService.getFilesStorageUrl()
        let cs = renderItem.classSchedule;

        let workoutImage = cs.workout.image ? fs + cs.workout.image : '/img/basezero_logo.png';
        let movementImage = cs.workout.movement?.image ? fs + cs.workout.movement.image : '/img/basezero_logo.jpeg';
        const info = renderItem.renderItemInfo;

        let screenData: MovementScreenData = {
            workoutImage: workoutImage,
            totalSets: info?.exerciseTask?.is_group_routine ? 1 : info?.exerciseTask?.exercises_total,
            podSets: info?.exerciseTask?.is_group_routine ? 1 : cs.workout.pod_config?.[0]?.laps[0]?.sets,
            laps: cs.workout.pod_config?.[0]?.laps?.length,
            hydrations: info?.exerciseTask?.hydrate_total,
            hydrationTime: cs.workout.hydration_break,
            exerciseTime: renderItem.work_time,
            restTime: renderItem?.rest_time,
            stations: cs.workout.workout_single_station ? cs.workout.workout_single_station.exercises_count : cs.workout.movement.stations,
            pods: cs.workout.movement?.pods || 1,
            movementImage: movementImage,
            name: cs.workout.name,
            description: cs.workout.description,
        };

        this.logger.debug('Rendered Timer next workout with data', screenData);

        return screenData ? screenData : {};
    }


    private renderAboutToStartWorkout(workoutRenderItem: RenderItem): WorkoutScreenData {
        if (!workoutRenderItem) {
            return {};
        }
        this.screenData = {
            header: this.composeWorkoutModulesHeader(workoutRenderItem),
            progressCircle: this.composeProgressCircle(workoutRenderItem),
            videos: this.composeWorkoutModulesVideos(workoutRenderItem),
        };

        return this.screenData ? this.screenData : {};

    }

    renderWorkout(item: RenderItem): WorkoutScreenData {
        // Check if the renderItemType is EXERCISE and there is exerciseTask info
        if (item.renderItemType !== RenderItemType.EXERCISE || !item.renderItemInfo || !item.renderItemInfo.exerciseTask) {
            return {};
        }

        const { exerciseTask } = item.renderItemInfo;
        const isWarmup = exerciseTask.type === 'warmup';
        const isGroupExercise = exerciseTask.type === 'group_exercise';
        const isWork = exerciseTask.type === 'work';
        const isGroupRoutine = exerciseTask.is_group_routine;
        const isRest = exerciseTask.type === 'rest';
        const isHydrate = exerciseTask.type === 'hydrate';
        const isStarting = exerciseTask.type === 'starting';
        const isIntroduction = exerciseTask.type === 'introduction';
        const isEnding = exerciseTask.type === 'ending';

        const changeStation = exerciseTask.station_change;
        const remainingSets = exerciseTask.remaining_sets;
        const totalSets = exerciseTask.current_set + exerciseTask.remaining_sets;

        const title = item.classSchedule?.workout.exercise_types.join(' + ') || '';
        const layoutType = item.classSchedule?.workout.movement?.movement_tv_layouts?.
            find(l => l.tv_id == this.configService.getTvId())?.layout_type;

        let workoutScreenData: WorkoutScreenData = {};


        if (isWarmup) {
            workoutScreenData = {
                type: 'warmup',
                // A.1: Show header without date and time
                header: {
                    show: false,
                    icon: '/img/warm-up.svg',
                    title: 'Warmup'
                },
                // A.2: Show progress circle with time and green color
                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    bypassCacheGuid: new Date().getTime().toString()
                },
                // A.3 & A.4: Show progress bar with stations unless station change is true
                progressBar: undefined,
                // A.4: Show notification when station change is true
                notification: undefined,
                // A.5: Show videos with stations
                videos: this.composeWorkoutModulesVideos(item)

            };
        }

        if (isGroupExercise) {
            workoutScreenData = {
                type: 'group_exercise',
                // A.1: Show header without date and time
                header: {
                    show: false,
                    icon: '/img/dumbbell.svg',
                    title: 'Group'
                },
                // A.2: Show progress circle with time and green color
                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    bypassCacheGuid: new Date().getTime().toString()
                },
                // A.3 & A.4: Show progress bar with stations unless station change is true
                progressBar: undefined,
                // A.4: Show notification when station change is true
                notification: undefined,
                // A.5: Show videos with stations
                videos: this.composeWorkoutModulesVideos(item)

            };
        }

        if (isWork) {
            workoutScreenData = {
                type: 'work',
                // A.1: Show header without date and time
                header: {
                    show: false,
                    icon: '/img/dumbbell.svg', // Example icon, you can adjust based on your use case
                    title: title,
                },
                // A.2: Show progress circle with time and green color
                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    bypassCacheGuid: new Date().getTime().toString()
                },
                // A.3 & A.4: Show progress bar with stations unless station change is true
                //progressBar: this.getProgressBar(item),
                // A.4: Show notification when station change is true
                notification: isGroupRoutine ? undefined : this.getNotificationMessage(item),
                // A.5: Show videos with stations
                videos: this.composeWorkoutModulesVideos(item)

            };
        }

        if (isRest) {
            workoutScreenData = {
                type: 'rest',
                // B.1: Show header without date and time
                header: {
                    show: false,
                    icon: '/img/dumbbell.svg', // Example icon
                    title: title
                },
                // B.2: Show progress circle with time and red color
                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    color: 'red'
                },
                // B.3: Always hide the progress bar
                progressBar: undefined,
                //sets
                sets: changeStation ? undefined : {
                    show: true,
                    total: totalSets,
                    active: exerciseTask.current_set
                },
                // B.4: Show stay module if station change is true
                stay: !changeStation ? {
                    show: !isGroupRoutine ? true : false,
                    message: 'station'
                } : undefined,
                changeStation: changeStation ? {
                    show: !isGroupRoutine ? true : false,
                    message: 'station_move'
                } : undefined,
                // B.6: Show notification with remaining sets
                notification: changeStation ? undefined : {
                    show: !isGroupRoutine ? true : false,
                    message: `${remainingSets}`
                },
                // B.7: Do not show videos
                videos: this.composeWorkoutModulesVideos(item)
            };
        }

        if (isHydrate) {
            workoutScreenData = {
                type: 'hydrate',
                // B.1: Show header without date and time
                header: {
                    show: true,
                    icon: '/img/dumbbell.svg', // Example icon
                    title: title
                },
                // B.2: Show progress circle with time and red color
                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    color: 'red'
                },
                batteryMedium: {
                    show: true,
                    message: 'recover'
                },
                progressBar: undefined,
                sets: undefined,
                stay: undefined,
                changeStation: undefined,
                notification: undefined,
                videos: this.composeWorkoutModulesVideos(item)
            };
        }

        if (isStarting || isIntroduction) {
            workoutScreenData = {
                type: 'starting',
                header: {
                    show: false,
                    icon: '/img/dumbbell.svg',
                    title: title
                },
                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    color: 'red'
                },
                progressBar: undefined,
                sets: undefined,
                stay: undefined,
                changeStation: undefined,
                notification: {
                    show: true,
                    message: isStarting ? 'get_ready' : 'introduction'
                },
                videos: this.composeWorkoutModulesVideos(item),
            };
        }

        if (isEnding) {
            workoutScreenData = {
                type: 'ending',
                header: {
                    show: true,
                    icon: '/img/dumbbell.svg',
                    title: title
                },
                progressCircle: {
                    show: true,
                    initialTotalTime: 0,
                    color: 'red'
                },
                progressBar: undefined,
                sets: undefined,
                stay: undefined,
                changeStation: undefined,
                notification: undefined,
                finish: {
                    show: true,
                    message: 'finish',
                    icon: item.classSchedule?.workout.image && item.classSchedule?.workout.image ?
                        this.configService.getFilesStorageUrl() + item.classSchedule?.workout.image
                        : undefined
                },
                videos: undefined
            };
        }

        if (workoutScreenData.type !== 'warmup') {
            // define layout type for every moment if it's not an warmup
            workoutScreenData.layoutType = layoutType;
        }

        return workoutScreenData;
    }


    renderTimerWorkout(item: RenderItem): TimerScreenData {

        // Check if the renderItemType is EXERCISE and there is exerciseTask info
        if (item.renderItemType !== RenderItemType.TIMER || !item.renderItemInfo || !item.renderItemInfo.exerciseTask) {
            return {};
        }

        const { exerciseTask } = item.renderItemInfo;

        // If the task used is the starting but is a transiction between group routines and the workout
        if (exerciseTask.type == 'starting' && exerciseTask.is_transaction) {
            exerciseTask.type = 'rest';
        }

        const isWarmUp = exerciseTask.type === 'warmup';
        const isGroupExercises = exerciseTask.type === 'group_exercise';
        const isWork = exerciseTask.type === 'work';
        const isRest = exerciseTask.type === 'rest';
        const isHydrate = exerciseTask.type === 'hydrate';
        const isStarting = exerciseTask.type === 'starting';
        const isIntroduction = exerciseTask.type === 'introduction';
        const isEnding = exerciseTask.type === 'ending';

        const title = item.classSchedule?.workout.exercise_types.join(' + ') || '';

        let workoutScreenData: TimerScreenData = {};
        const isGroupRoutine = isGroupExercises || isWarmUp;
        if (isWork || isRest || isHydrate || isGroupRoutine) {
            workoutScreenData = {
                isWorkout: true,
                isWorkoutStarting: false,
                workoutName: title,

                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    color: isRest ? 'red' : 'green',
                    bypassCacheGuid: new Date().getTime().toString()
                },

                totalTime: exerciseTask.total_time || exerciseTask.time,
                currentExercise: exerciseTask.exercises_accumulated || 1,
                totalExercises: exerciseTask.exercises_total || 1,
                totalSets: exerciseTask.is_group_routine ? 1 : exerciseTask.exercises_total,
                totalPods: item.classSchedule?.workout?.movement?.pods || 1,
                currentSetTime: exerciseTask.time,
                currentPod: exerciseTask.current_pod,
                hydrate_active: exerciseTask.hydrate_active,
                hydrate_total: exerciseTask.hydrate_total,
                type: exerciseTask.type,
                podDuration: exerciseTask.is_transaction || isGroupRoutine ? 0 : exerciseTask.podDuration

            };
        }

        if (isStarting || isIntroduction) {
            workoutScreenData = {
                isWorkout: true,
                isWorkoutStarting: isStarting,
                isWorkoutIntroduction: isIntroduction,
                progressCircle: {
                    show: true,
                    initialTotalTime: item.renderItemInfo.duration || exerciseTask.time,
                    color: 'red'
                },

            };
        }

        if (isEnding) {
            workoutScreenData = {
                isWorkout: true,
                isWorkoutStarting: false,
                progressCircle: undefined,
                workoutName: title,
                currentExercise: exerciseTask.exercises_total,
                totalExercises: exerciseTask.exercises_total,
                type: exerciseTask.type,
            };
        }

        return workoutScreenData;
    }


    private composeWorkoutModulesPersonalTrainer(workoutRenderItem: RenderItem): WorkoutModulesPersonalTrainer {
        const cs = workoutRenderItem.classSchedule;
        if (!cs) {
            return {
                show: false,
                name: "",
                job_title: "",
                photo: "",
            };
        }

        const show = workoutRenderItem.renderItemType === RenderItemType.EXERCISE_NEXT_WORKOUT ? true : false;
        const trainerPhoto = this.getTrainerPhoto(cs);

        const workoutModulesPersonalTrainer: WorkoutModulesPersonalTrainer = {
            show: show,
            name: cs.trainer?.name,
            job_title: cs.trainer?.job_title,
            photo: trainerPhoto,
        };
        return workoutModulesPersonalTrainer;
    }

    private composeWorkoutModulesVideos(renderItem: RenderItem): WorkoutModulesVideos {
        const vid: WorkoutModulesVideos = {
            show: false,
            showStations: false,
            exercisesStation: [],
            isGroupRoutine: false
        };

        const classSchedule = renderItem.classSchedule;

        if (!classSchedule) {
            return {
                show: false,
                showStations: false,
                exercisesStation: []
            };
        }

        const isRestingTask = renderItem.renderItemInfo?.exerciseTask?.type === 'rest' || renderItem.renderItemInfo?.exerciseTask?.type === 'hydrate';

        if (renderItem.renderItemType === RenderItemType.EXERCISE_NEXT_WORKOUT) {
            vid.show = true;
            vid.isGroupRoutine = renderItem.classSchedule?.workout?.workout_single_station ? true : false;
            vid.showStations = vid.isGroupRoutine ? false : true;;
            vid.exercisesStation = this.composeExerciseInfo(classSchedule);
        } else if (renderItem.renderItemType === RenderItemType.EXERCISE_ABOUT_TO_START) {
            vid.show = true;
            vid.showStations = true;
            vid.exercisesStation = this.composeExerciseInfo(classSchedule);
        } else if (renderItem.renderItemType === RenderItemType.EXERCISE) {
            let show = true;

            if (isRestingTask) {
                show = false;
            }
            const isWarmUp = renderItem.renderItemInfo?.exerciseTask?.type === 'warmup';
            const isGoupExercises = renderItem.renderItemInfo?.exerciseTask?.type === 'group_exercise';
            const isGroupRoutine = isWarmUp
                || renderItem.renderItemInfo?.exerciseTask?.is_group_routine
                || isGoupExercises;

            vid.show = show;
            vid.showStations = isGroupRoutine ? false : true;
            vid.exercisesStation = this.composeExerciseInfo(classSchedule, renderItem.renderItemInfo?.exerciseTask);
            vid.isGroupRoutine = isGroupRoutine;
        }

        if (vid.isGroupRoutine) {
            let currSet = renderItem.renderItemInfo?.exerciseTask?.exercises_accumulated || 1;
            // on resting tasks on a group routine we want to move the next exercise to the top
            currSet = isRestingTask ? currSet + 1 : currSet;

            // Performance wise: only send the number of videos that will be present on the screen
            vid.exercisesStation = vid.exercisesStation?.slice(currSet - 1, currSet + 2);


        }

        return vid;
    }


    private composeProgressCircle(renderItem: RenderItem): WorkoutModulesProgressCircle {
        const pg: WorkoutModulesProgressCircle = {
            show: false,
            initialTotalTime: 0,
        };
        if (!renderItem) {
            return pg;
        }

        if (renderItem.renderItemType === RenderItemType.EXERCISE_NEXT_WORKOUT) {
            pg.show = true;
            pg.initialTotalTime = renderItem.duration ? renderItem.duration : 0;
        } else if (renderItem.renderItemType === RenderItemType.EXERCISE_ABOUT_TO_START) {
            pg.show = true;
            pg.initialTotalTime = renderItem.duration ? renderItem.duration : 0;
        } else if (renderItem.renderItemType === RenderItemType.EXERCISE) {
            pg.show = true;
            pg.initialTotalTime = renderItem.renderItemInfo?.exerciseTask?.time || 0;
            pg.color = renderItem.renderItemInfo?.exerciseTask?.type === 'work' ? 'green' : 'red';
        }

        return pg;
    }

    private composeWorkoutModulesHeader(renderItem: RenderItem): WorkoutModulesHeader {
        const cs = renderItem.classSchedule;
        const header: WorkoutModulesHeader = {
            show: false,
            icon: "",
            title: "",
            date: "",
            time: "",
        };

        if (!cs) {
            return header
        }

        if (renderItem.renderItemType === RenderItemType.EXERCISE_NEXT_WORKOUT) {
            header.show = true;
            header.icon = "/img/goal.svg";
            header.title = cs.workout.exercise_types.join(' + ');
            header.date = renderItem.date;
            header.time = renderItem.time;
        } else if (renderItem.renderItemType === RenderItemType.EXERCISE_ABOUT_TO_START) {
            header.show = false;
            header.icon = "/img/goal.svg";
            header.title = cs.workout.exercise_types.join(' + ');
            header.date = renderItem.date;
            header.time = renderItem.time;

        } else if (renderItem.renderItemType === RenderItemType.EXERCISE) {
            header.show = false;
            header.icon = "/img/dumbbell.svg";
            header.title = cs.workout.exercise_types.join(' + ');
            header.date = "";
            header.time = "";
        };

        return header;
    }

    private composeExerciseInfo(classSchedule: ClassInstance, task?: ExerciseTask): ExerciseStationVideo[] {
        const exerciseInfoList: ExerciseStationVideo[] = [];
        const workout = classSchedule?.workout;
        if (workout) {

            const isWarmup = task?.type == 'warmup';
            const isGroupRoutine = task?.type == 'group_exercise';

            let exercises: (Exercise & { station_number?: ExerciseStation['station_number'] })[] = [];
            if (isWarmup) {
                exercises = workout.warmup?.exercises
            } else if (isGroupRoutine) {
                exercises = workout.workout_group_routines.find(wgr => wgr.phase == task.phase && wgr.group_routine.id == task.routine_id)?.group_routine?.exercises!;
            } else {
                exercises = workout.exercises?.map(exerciseItem => ({
                    ...exerciseItem.exercise,
                    station_number: exerciseItem.station_number
                })) || [];
            }

            for (const exercise of exercises) {
                exerciseInfoList.push({
                    id: exercise.id,
                    name: exercise.name,
                    video: this.configService.getFilesStorageUrl() + exercise.video,
                    station_number: exercise.station_number?.toString(),
                    type: exercise.type
                });
            }
        }
        return exerciseInfoList;
    }
    // #endregion


    // #region Aux Methods

    private getTrainerPhoto(cs: ClassInstance) {
        return (this.configService.getFilesStorageUrl() && cs.trainer?.profile_image) ? this.configService.getFilesStorageUrl() + cs.trainer.profile_image : undefined;
    }

    private getNotificationMessage(item: RenderItem): WorkoutModulesNotification {

        let notification: WorkoutModulesNotification = {
            show: false,
            message: '',
            time: 0,
            showOnTimeLeft: 0,
        };

        if (!item.renderItemInfo || !item.renderItemInfo.exerciseTask) {
            return notification;
        }

        notification.show = true;
        notification.showOnTimeLeft = 15;
        notification.time = item.renderItemInfo.exerciseTask.time;

        if (item.renderItemInfo.exerciseTask.pod_change) {
            notification.message = 'set_pod_move';
        }
        else if (!item.renderItemInfo.exerciseTask.station_change && item.renderItemInfo.exerciseTask.remaining_sets !== 0) {
            notification.message = 'set_station';
        } else if (item.renderItemInfo.exerciseTask.station_change && item.renderItemInfo.exerciseTask.remaining_sets === 0) {
            notification.message = 'set_station_move';
        }

        return notification;
    }
    // #endregion
}
